/**
 * Modern UI Theme for RollWords
 * Provides consistent colors, typography, and component styles
 */

export const COLORS = {
  // Primary palette
  primary: {
    main: 0x6366f1,      // Indigo
    light: 0x818cf8,     // Light indigo
    dark: 0x4f46e5,      // Dark indigo
    text: '#6366f1',
  },
  
  // Secondary palette
  secondary: {
    main: 0x8b5cf6,      // Purple
    light: 0xa78bfa,     // Light purple
    dark: 0x7c3aed,      // Dark purple
    text: '#8b5cf6',
  },
  
  // Success
  success: {
    main: 0x10b981,      // Emerald
    light: 0x34d399,     // Light emerald
    dark: 0x059669,      // Dark emerald
    text: '#10b981',
    bg: '#064e3b',       // Dark emerald background
  },
  
  // Warning
  warning: {
    main: 0xf59e0b,      // Amber
    light: 0xfbbf24,     // Light amber
    dark: 0xd97706,      // Dark amber
    text: '#f59e0b',
    bg: '#451a03',       // Dark amber background
  },
  
  //Error
  error: {
    main: 0xef4444,      // Red
    light: 0xf87171,     // Light red
    dark: 0xdc2626,      // Dark red
    text: '#ef4444',
    bg: '#450a0a',       // Dark red background
  },
  
  // Info
  info: {
    main: 0x06b6d4,      // Cyan
    light: 0x22d3ee,     // Light cyan
    dark: 0x0891b2,      // Dark cyan
    text: '#06b6d4',
    bg: '#164e63',       // Dark cyan background
  },
  
  // Neutrals
  background: {
    primary: 0x0f172a,   // Slate 900
    secondary: 0x1e293b, // Slate 800
    tertiary: 0x334155,  // Slate 700
    paper: 0x1e293b,     // Card/panel background
    surface: 0x334155,   // Raised surface elements
  },
  
  text: {
    primary: '#f1f5f9',  // Slate 100
    secondary: '#cbd5e1', // Slate 300
    disabled: '#64748b',  // Slate 500
    accent: '#fbbf24',    // Amber (for highlights)
  },
  
  border: {
    light: 0x475569,     // Slate 600
    main: 0x334155,      // Slate 700
    dark: 0x1e293b,      // Slate 800
  },
  
  // Game-specific
  board: {
    background: 0x1e293b,
    cell: 0x0f172a,
    grid: 0x334155,
    center: 0xfbbf24,    // Amber for center square
  },
  
  // Player colors (vibrant and distinct)
  players: [
    { primary: 0x3b82f6, light: 0x60a5fa, name: '#3b82f6' }, // Blue
    { primary: 0xef4444, light: 0xf87171, name: '#ef4444' }, // Red
    { primary: 0x10b981, light: 0x34d399, name: '#10b981' }, // Green
    { primary: 0xf59e0b, light: 0xfbbf24, name: '#f59e0b' }, // Amber
  ],
};

export const TYPOGRAPHY = {
  // Font family - clean modern sans-serif
  fontFamily: {
    default: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  
  // Font sizes
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
    '6xl': '60px',
  },
  
  fontWeight: {
  // Font weights
    normal: 'normal',
    medium: '500',
    semibold: '600',
    bold: 'bold',
  },
  
  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

export const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  '4xl': 64,
};

export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
};

export const BORDER_RADIUS = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

/**
 * Pre-built component styles
 */
export const COMPONENT_STYLES = {
  // Primary button
  buttonPrimary: {
    fontSize: TYPOGRAPHY.fontSize.lg,
    color: '#ffffff',
    padding: { x: SPACING.xl, y: SPACING.md },
    backgroundColor: '#6366f1',
  },
  
  // Secondary button
  buttonSecondary: {
    fontSize: TYPOGRAPHY.fontSize.lg,
    color: '#ffffff',
    padding: { x: SPACING.xl, y: SPACING.md },
    backgroundColor: '#64748b',
  },
  
  // Success button
  buttonSuccess: {
    fontSize: TYPOGRAPHY.fontSize.lg,
    color: '#ffffff',
    padding: { x: SPACING.xl, y: SPACING.md },
    backgroundColor: '#10b981',
  },
  
  // Card title
  cardTitle: {
    fontSize: TYPOGRAPHY.fontSize['2xl'],
    color: COLORS.text.primary,
  },
  
  // Card text
  cardText: {
    fontSize: TYPOGRAPHY.fontSize.base,
    color: COLORS.text.secondary,
  },
  
  // Heading 1
  h1: {
    fontSize: TYPOGRAPHY.fontSize['5xl'],
    color: COLORS.text.primary,
  },
  
  // Heading 2
  h2: {
    fontSize: TYPOGRAPHY.fontSize['4xl'],
    color: COLORS.text.primary,
  },
  
  // Heading 3
  h3: {
    fontSize: TYPOGRAPHY.fontSize['3xl'],
    color: COLORS.text.primary,
  },
  
  // Body text
  body: {
    fontSize: TYPOGRAPHY.fontSize.base,
    color: COLORS.text.primary,
  },
  
  // Small text
  small: {
    fontSize: TYPOGRAPHY.fontSize.sm,
    color: COLORS.text.secondary,
  },
  
  // Instructions
  instructions: {
    fontSize: TYPOGRAPHY.fontSize.sm,
    color: COLORS.text.secondary,
    backgroundColor: '#1e293b',
    padding: { x: SPACING.md, y: SPACING.sm },
  },
};

/**
 * Helper to create a rounded rectangle button with modern styling
 */
export function createModernButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  text: string,
  style: 'primary' | 'secondary' | 'success' = 'primary'
): { bg: Phaser.GameObjects.Graphics; text: Phaser.GameObjects.Text } {
  const colors = {
    primary: { main: COLORS.primary.main, hover: COLORS.primary.light, active: COLORS.primary.dark },
    secondary: { main: 0x64748b, hover: 0x475569, active: 0x334155 },
    success: { main: COLORS.success.main, hover: COLORS.success.light, active: COLORS.success.dark },
  };
  
  const color = colors[style];
  const graphics = scene.add.graphics();
  
  // Draw rounded rectangle
  graphics.fillStyle(color.main, 1);
  graphics.fillRoundedRect(x - width / 2, y - height / 2, width, height, BORDER_RADIUS.md);
  
  // Add subtle shadow effect with a second darker rectangle
  const shadowGraphics = scene.add.graphics();
  shadowGraphics.fillStyle(0x000000, 0.2);
  shadowGraphics.fillRoundedRect(x - width / 2 + 2, y - height / 2 + 2, width, height, BORDER_RADIUS.md);
  shadowGraphics.setDepth(graphics.depth - 1);
  
  const textObj = scene.add.text(x, y, text, {
    fontSize: TYPOGRAPHY.fontSize.lg,
    color: '#ffffff',
  }).setOrigin(0.5);
  
  return { bg: graphics, text: textObj };
}

/**
 * Helper to create a card/panel with modern styling
 */
export function createCard(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  depth: number = 1
): Phaser.GameObjects.Graphics {
  const graphics = scene.add.graphics();
  
  // Shadow
  graphics.fillStyle(0x000000, 0.3);
  graphics.fillRoundedRect(x + 4, y + 4, width, height, BORDER_RADIUS.lg);
  
  // Main card
  graphics.fillStyle(COLORS.background.paper, 1);
  graphics.fillRoundedRect(x, y, width, height, BORDER_RADIUS.lg);
  
  // Border
  graphics.lineStyle(2, COLORS.border.main, 1);
  graphics.strokeRoundedRect(x, y, width, height, BORDER_RADIUS.lg);
  
  graphics.setDepth(depth);
  
  return graphics;
}
